/**
 * DecisionFlow — SettingsPanel
 * ui/src/components/admin/SettingsPanel.jsx
 * S552 — Priority 3
 *
 * Route: /admin/settings  (gear icon at CompAdminShell rail footer)
 *
 * Sections (mastercontext_s552.md §Settings Spec):
 *   Licensing                      — fully built (Super Admin: full; Comp Admin: read + request)
 *   Localization                   — fully built
 *   Acknowledgment Thresholds      — S616 NEW — comp_admin + super_admin
 *   Support & Diag                 — fully built (Comp Admin: tenant ID + version only)
 *   AI & Agent                     — stub with correct header
 *   Integrations                   — stub with correct header
 *   Identity & Access              — stub with correct header
 *   Branding                       — stub (Super Admin only — hidden from Comp Admin)
 *
 * Role-aware visibility (mastercontext_s552.md §Settings role table):
 *   Super Admin (tenant_super_admin): sees all sections, full edit
 *   Comp Admin  (comp_admin):         Licensing (read+request) · Localization (personal)
 *                                     Acknowledgment Thresholds (edit) · Support (subset)
 *                                     Integrations (read-only) · Identity & Access (read-only)
 *                                     NO Branding
 *
 * Controls (non-negotiable):
 *   CSS: rgb(var(--color-*)) only — no hardcoded hex
 *   Modal: does not close on outside click
 *   Error: resp.json()["detail"]["error"]["code"]
 *   Tables: max-h-* + overflow-y-auto + sticky thead
 *   No <form> tags
 */

import { useState, useEffect } from "react";
import {
  BadgeDollarSign, Globe, Sparkles, Plug, ShieldCheck, LifeBuoy,
  Palette, ChevronDown, ChevronUp, Lock, CheckCircle2, XCircle,
  AlertTriangle, RefreshCw, ListChecks, Clock,
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext.jsx";
import Card from "../shared/Card.jsx";
import Button from "../shared/Button.jsx";
import Pill from "../shared/Pill.jsx";

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function useFetch(url) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!url) { setLoading(false); return; }
    setLoading(true);
    fetch(url, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [url]);

  return { data, loading, error };
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-4 flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
        <Icon className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
      </div>
      <div>
        <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">{title}</h2>
        {subtitle && <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text-muted))]">{subtitle}</p>}
      </div>
    </div>
  );
}

function FieldRow({ label, value, children }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-[rgb(var(--color-border))] last:border-0">
      <span className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))] shrink-0 w-48">{label}</span>
      <div className="text-tok-sm text-[rgb(var(--color-text))] flex-1 text-right">
        {children ?? value ?? "—"}
      </div>
    </div>
  );
}

function StubSection({ icon, title, subtitle, note }) {
  return (
    <Card>
      <SectionHeader icon={icon} title={title} subtitle={subtitle} />
      <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-6 text-center">
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
          {note ?? "Configuration available. Full build in a future session."}
        </p>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Request More Licenses Modal
// ---------------------------------------------------------------------------
function RequestLicensesModal({ onClose }) {
  const [quantity,  setQuantity]  = useState(1);
  const [reason,    setReason]    = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState(null);

  async function handleSubmit() {
    if (!reason.trim() || quantity < 1) return;
    setSubmitting(true);
    setError(null);
    try {
      const res  = await fetch(`${API_BASE}/licensing/requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ quantity, justification: reason.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.detail?.error?.code ?? "LICENSE_REQUEST_ERROR";
        throw new Error(`[${code}] ${body?.detail?.error?.message ?? `HTTP ${res.status}`}`);
      }
      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  // Modal does NOT close on outside click (NN-07)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6 shadow-xl">
        <h2 className="mb-4 text-tok-base font-bold text-[rgb(var(--color-text))]">
          Request More Licenses
        </h2>

        {submitted ? (
          <>
            <div className="mb-4 flex items-center gap-3 rounded-xl border border-[rgb(var(--color-success))]/40 bg-[rgb(var(--color-success))]/8 px-4 py-3">
              <CheckCircle2 className="h-5 w-5 text-[rgb(var(--color-success))]" />
              <p className="text-tok-sm text-[rgb(var(--color-success))]">
                Request submitted — your Super Admin will be notified for approval.
              </p>
            </div>
            <div className="flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </>
        ) : (
          <>
            <p className="mb-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
              Submit a request to your Super Admin to increase licensed seat counts.
            </p>

            <div className="mb-3 space-y-1">
              <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                Additional seats requested
              </label>
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
              />
            </div>

            <div className="mb-4 space-y-1">
              <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                Business justification
              </label>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain why additional licenses are needed…"
                className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none resize-none"
              />
            </div>

            {error && (
              <div className="mb-3 flex items-start gap-2 rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-3 py-2.5 text-tok-sm text-[rgb(var(--color-danger))]">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting || !reason.trim()}>
                {submitting ? "Submitting…" : "Submit Request"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Licensing Section
// ---------------------------------------------------------------------------
function LicensingSection({ role, tenantContext }) {
  const isSuperAdmin = role === "tenant_super_admin";
  const [showRequestModal, setShowRequestModal] = useState(false);

  const loading = !tenantContext;
  const error   = null;

  const mode        = tenantContext?.subscription_mode ?? null;
  const tier        = tenantContext?.subscription_tier ?? null;
  const trialExpiry = tenantContext?.trial_expires_at  ?? null;
  const trialDays   = tenantContext?.trial_days_remaining ?? null;

  const seats        = tenantContext?.seats ?? null;
  const repsUsed     = seats?.current_positions    ?? null;
  const repsTotal    = seats?.max_positions        ?? null;
  const adminsUsed   = seats?.current_comp_admins  ?? null;
  const adminsTotal  = seats?.max_comp_admins      ?? null;
  const financeUsed  = seats?.current_finance_users ?? null;
  const financeTotal = seats?.max_finance_users     ?? null;
  const entitlements = tenantContext?.entitlements  ?? [];

  const daysUrgency =
    trialDays === null ? null :
    trialDays === 0   ? "text-[rgb(var(--color-danger))]" :
    trialDays <= 3    ? "text-[rgb(var(--color-danger))]" :
    trialDays <= 7    ? "text-[rgb(var(--color-warning))]" :
                        "text-[rgb(var(--color-text-muted))]";

  return (
    <Card>
      <SectionHeader
        icon={BadgeDollarSign}
        title="Licensing"
        subtitle="Subscription status, seat counts, and feature entitlements for this tenant."
      />

      {loading && (
        <div className="animate-pulse space-y-2">
          <div className="h-4 w-2/3 rounded skeleton" />
          <div className="h-4 w-1/2 rounded skeleton" />
        </div>
      )}

      {error && !loading && (
        <div className="rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-3 py-2.5 text-tok-sm text-[rgb(var(--color-danger))]">
          Unable to load licensing data.
        </div>
      )}

      {!loading && (
        <div className="divide-y divide-[rgb(var(--color-border))] rounded-xl border border-[rgb(var(--color-border))]">
          <div className="px-4 py-1">
            <FieldRow label="Subscription mode">
              <Pill tone={mode === "trial" ? "Warning" : mode === "paid" ? "Complete" : "Draft"}>
                {mode ?? "—"}
              </Pill>
            </FieldRow>
            <FieldRow label="Tier" value={tier ?? "—"} />

            {mode === "trial" && trialDays !== null && (
              <FieldRow label="Trial expiry">
                <span className={`font-medium ${daysUrgency}`}>
                  {trialDays === 0
                    ? "Expired"
                    : `${trialDays} day${trialDays !== 1 ? "s" : ""} remaining`}
                  {trialExpiry && ` · ${new Date(trialExpiry).toLocaleDateString()}`}
                </span>
              </FieldRow>
            )}

            <FieldRow label="Licensed reps (positions)">
              {repsUsed !== null
                ? `${repsUsed} used / ${repsTotal !== null ? repsTotal + " available" : "Unlimited"}`
                : "—"}
            </FieldRow>

            <FieldRow label="Licensed comp admins">
              {adminsUsed !== null
                ? `${adminsUsed} used / ${adminsTotal !== null ? adminsTotal + " available" : "Unlimited"}`
                : "—"}
            </FieldRow>

            <FieldRow label="Licensed finance users">
              {financeUsed !== null
                ? `${financeUsed} used / ${financeTotal !== null ? financeTotal + " available" : "Unlimited"}`
                : "—"}
            </FieldRow>
          </div>
        </div>
      )}

      {!loading && entitlements.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
            Feature Entitlements
          </p>
          <div className="max-h-64 overflow-y-auto rounded-xl border border-[rgb(var(--color-border))]">
            <table className="w-full text-tok-sm">
              <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))]">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Feature</th>
                  <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Tier</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))]">
                {entitlements.map((e) => (
                  <tr key={e.feature_key} className="bg-[rgb(var(--color-surface))] hover:bg-[rgb(var(--color-surface-muted))]">
                    <td className="px-3 py-2 text-[rgb(var(--color-text))]">{e.feature_label ?? e.feature_key}</td>
                    <td className="px-3 py-2">
                      {e.enabled ? (
                        <div className="flex items-center gap-1.5 text-[rgb(var(--color-success))]">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Enabled
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-[rgb(var(--color-text-muted))]">
                          <Lock className="h-3.5 w-3.5" /> Locked
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
                      {e.required_tier ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="ghost" onClick={() => setShowRequestModal(true)}>
          Request More Licenses
        </Button>
        {isSuperAdmin && mode === "trial" && (
          <Button>Upgrade to Paid</Button>
        )}
      </div>

      {showRequestModal && (
        <RequestLicensesModal onClose={() => setShowRequestModal(false)} />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Localization Section
// ---------------------------------------------------------------------------
function LocalizationSection({ role }) {
  const isSuperAdmin = role === "tenant_super_admin";

  const LOCALES = [
    { value: "en-US", label: "English (US)" },
    { value: "en-GB", label: "English (UK)" },
    { value: "fr-FR", label: "French (France)" },
    { value: "de-DE", label: "German (Germany)" },
    { value: "ja-JP", label: "Japanese" },
    { value: "pt-BR", label: "Portuguese (Brazil)" },
  ];

  const CURRENCIES = ["USD", "EUR", "GBP", "JPY", "BRL", "CAD", "AUD"];
  const DATE_FORMATS = ["MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"];

  const { data: localeData, loading } = useFetch(`${API_BASE}/tenant-settings/localization`);

  const [locale,      setLocale]      = useState("");
  const [currency,    setCurrency]    = useState("");
  const [dateFormat,  setDateFormat]  = useState("");
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [saveError,   setSaveError]   = useState(null);

  useState(() => {
    if (!localeData) return;
    setLocale(localeData.locale     ?? "en-US");
    setCurrency(localeData.currency ?? "USD");
    setDateFormat(localeData.date_format ?? "MM/DD/YYYY");
  }, [localeData]);

  async function handleSave() {
    setSaving(true); setSaved(false); setSaveError(null);
    try {
      const endpoint = isSuperAdmin
        ? `${API_BASE}/tenant-settings/localization`
        : `${API_BASE}/user-settings/localization`;
      const res  = await fetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ locale, currency, date_format: dateFormat }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.detail?.error?.code ?? "SAVE_ERROR";
        throw new Error(`[${code}] ${body?.detail?.error?.message ?? `HTTP ${res.status}`}`);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <SectionHeader
        icon={Globe}
        title="Localization"
        subtitle={
          isSuperAdmin
            ? "Tenant-wide locale, currency, and date format defaults."
            : "Personal locale override. Tenant-wide defaults are set by your Super Admin."
        }
      />

      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-4 w-2/3 rounded skeleton" />
          <div className="h-4 w-1/2 rounded skeleton" />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
              {isSuperAdmin ? "Tenant locale" : "Personal locale override"}
            </label>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            >
              {LOCALES.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>

          {isSuperAdmin && (
            <>
              <div className="space-y-1">
                <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Default currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Date format</label>
                <select
                  value={dateFormat}
                  onChange={(e) => setDateFormat(e.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
                >
                  {DATE_FORMATS.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {saveError && (
            <div className="rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-3 py-2.5 text-tok-sm text-[rgb(var(--color-danger))]">
              {saveError}
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
            {saved && (
              <span className="flex items-center gap-1.5 text-tok-sm text-[rgb(var(--color-success))]">
                <CheckCircle2 className="h-4 w-4" /> Saved
              </span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Acknowledgment Thresholds Section — S616
// ---------------------------------------------------------------------------
// Visible to comp_admin and tenant_super_admin.
// Reads platform defaults (scope_type=platform) + tenant overrides (scope_type=tenant).
// Tenant row wins. Save: POST if no tenant row yet, PATCH if one exists.
// parameter_keys:
//   ack_coverage_green_threshold  — integer, default 85
//   ack_coverage_amber_threshold  — integer, default 50
// ---------------------------------------------------------------------------
function AcknowledgmentThresholdsSection() {
  const PLATFORM_DEFAULTS = { green: 85, amber: 50 };

  // Fetch platform defaults (tenant_id IS NULL rows — readable via API)
  const { data: platformData, loading: platformLoading } =
    useFetch(`${API_BASE}/parameter-config?scope_type=platform&limit=200`);

  // Fetch tenant overrides
  const [tenantRefreshKey, setTenantRefreshKey] = useState(0);
  const { data: tenantData, loading: tenantLoading } =
    useFetch(`${API_BASE}/parameter-config?scope_type=tenant&limit=200&_k=${tenantRefreshKey}`);

  // Resolve platform defaults from API response
  const platformRows = platformData?.data ?? [];
  const platformGreen = parseInt(
    platformRows.find((r) => r.parameter_key === "ack_coverage_green_threshold")?.parameter_value
    ?? PLATFORM_DEFAULTS.green,
    10,
  );
  const platformAmber = parseInt(
    platformRows.find((r) => r.parameter_key === "ack_coverage_amber_threshold")?.parameter_value
    ?? PLATFORM_DEFAULTS.amber,
    10,
  );

  // Resolve tenant overrides — extract existing row IDs for PATCH
  const tenantRows = tenantData?.data ?? [];
  const tenantGreenRow = tenantRows.find((r) => r.parameter_key === "ack_coverage_green_threshold");
  const tenantAmberRow = tenantRows.find((r) => r.parameter_key === "ack_coverage_amber_threshold");

  // Local edit state — initialised from tenant row if present, else platform default
  const [green, setGreen] = useState("");
  const [amber, setAmber] = useState("");
  const [initialised, setInitialised] = useState(false);

  useEffect(() => {
    if (tenantLoading || platformLoading || initialised) return;
    setGreen(String(tenantGreenRow ? parseInt(tenantGreenRow.parameter_value, 10) : platformGreen));
    setAmber(String(tenantAmberRow ? parseInt(tenantAmberRow.parameter_value, 10) : platformAmber));
    setInitialised(true);
  }, [tenantLoading, platformLoading, tenantGreenRow, tenantAmberRow, platformGreen, platformAmber, initialised]);

  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [saveError, setSaveError] = useState(null);

  function validate() {
    const g = parseInt(green, 10);
    const a = parseInt(amber, 10);
    if (isNaN(g) || g < 1 || g > 100) return "Green threshold must be between 1 and 100.";
    if (isNaN(a) || a < 1 || a > 100) return "Amber threshold must be between 1 and 100.";
    if (a >= g) return "Amber threshold must be less than the green threshold.";
    return null;
  }

  async function upsertParam(key, value, existingRow) {
    if (existingRow) {
      // PATCH existing tenant row
      const res = await fetch(`${API_BASE}/parameter-config/${existingRow.parameter_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ parameter_value: String(value) }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.detail?.error?.code ?? "SAVE_ERROR";
        throw new Error(`[${code}] ${body?.detail?.error?.message ?? `HTTP ${res.status}`}`);
      }
    } else {
      // POST new tenant row
      const res = await fetch(`${API_BASE}/parameter-config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          parameter_key:   key,
          parameter_value: String(value),
          value_type:      "integer",
          scope_type:      "tenant",
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.detail?.error?.code ?? "SAVE_ERROR";
        throw new Error(`[${code}] ${body?.detail?.error?.message ?? `HTTP ${res.status}`}`);
      }
    }
  }

  async function handleSave() {
    const err = validate();
    if (err) { setSaveError(err); return; }

    setSaving(true);
    setSaved(false);
    setSaveError(null);

    try {
      await upsertParam("ack_coverage_green_threshold", parseInt(green, 10), tenantGreenRow);
      await upsertParam("ack_coverage_amber_threshold", parseInt(amber, 10), tenantAmberRow);
      setSaved(true);
      setInitialised(false); // trigger re-read on next tenant fetch
      setTenantRefreshKey((k) => k + 1);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setGreen(String(platformGreen));
    setAmber(String(platformAmber));
    setSaveError(null);
  }

  const isLoading = platformLoading || tenantLoading;
  const hasTenantOverride = !!(tenantGreenRow || tenantAmberRow);

  return (
    <Card>
      <SectionHeader
        icon={ListChecks}
        title="Acknowledgment Thresholds"
        subtitle="Configure the coverage % thresholds that drive the period readiness indicator on the Readiness screen."
      />

      {isLoading && (
        <div className="animate-pulse space-y-2">
          <div className="h-4 w-2/3 rounded skeleton" />
          <div className="h-4 w-1/2 rounded skeleton" />
        </div>
      )}

      {!isLoading && (
        <div className="space-y-5">
          {/* Indicator legend */}
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 space-y-2">
            <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
              How the indicator works
            </p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-tok-sm">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[rgb(var(--color-success))]" />
                <span className="text-[rgb(var(--color-text))]">
                  Green — ≥ green threshold % acknowledged
                </span>
              </div>
              <div className="flex items-center gap-2 text-tok-sm">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[rgb(var(--color-warning))]" />
                <span className="text-[rgb(var(--color-text))]">
                  Amber — ≥ amber threshold but below green threshold
                </span>
              </div>
              <div className="flex items-center gap-2 text-tok-sm">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[rgb(var(--color-danger))]" />
                <span className="text-[rgb(var(--color-text))]">
                  Red — below amber threshold
                </span>
              </div>
            </div>
          </div>

          {/* Threshold inputs */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                Green threshold (%)
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={green}
                onChange={(e) => { setGreen(e.target.value); setSaveError(null); }}
                className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
              />
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                Platform default: {platformGreen}%
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                Amber threshold (%)
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={amber}
                onChange={(e) => { setAmber(e.target.value); setSaveError(null); }}
                className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
              />
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                Platform default: {platformAmber}%
              </p>
            </div>
          </div>

          {hasTenantOverride && (
            <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-primary))]/30 bg-[rgb(var(--color-primary))]/5 px-3 py-2 text-tok-xs text-[rgb(var(--color-primary))]">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              Tenant-specific thresholds are active — platform defaults are overridden.
            </div>
          )}

          {saveError && (
            <div className="flex items-start gap-2 rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-3 py-2.5 text-tok-sm text-[rgb(var(--color-danger))]">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{saveError}</span>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save thresholds"}
            </Button>
            {hasTenantOverride && (
              <Button variant="ghost" onClick={handleReset} disabled={saving}>
                Reset to platform defaults
              </Button>
            )}
            {saved && (
              <span className="flex items-center gap-1.5 text-tok-sm text-[rgb(var(--color-success))]">
                <CheckCircle2 className="h-4 w-4" /> Saved
              </span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Identity & Access Section — S903
// ---------------------------------------------------------------------------
// Session idle timeout — tenant-wide setting, super_admin edit only.
// comp_admin: read-only view of current effective timeout.
// Storage: parameter_config, parameter_key='session_idle_timeout_minutes',
//   scope_type='tenant'. Platform default: 30 minutes. Range: 5–480 min.
// Matches AuthContext.jsx idleTimeoutRef — value loaded there at boot.
// ---------------------------------------------------------------------------
function IdentityAccessSection({ role }) {
  const isSuperAdmin = role === "tenant_super_admin";
  // comp_admin is the operational owner — they feel session timeout pain directly.
  // Same edit scope as AcknowledgmentThresholdsSection. S903 scope correction.
  const canEdit      = isSuperAdmin || role === "comp_admin";
  const PLATFORM_DEFAULT_FALLBACK = 30;  // last-resort if DB row also absent

  const { data, loading, error } = useFetch(`${API_BASE}/tenant-settings/session`);

  // platform_default comes from DB (m_s903_01 seed). JS constant is final fallback only.
  const platformDefault = data?.platform_default ?? PLATFORM_DEFAULT_FALLBACK;

  const [minutes,     setMinutes]     = useState("");
  const [initialised, setInitialised] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [saveError,   setSaveError]   = useState(null);

  useEffect(() => {
    if (loading || initialised || !data) return;
    setMinutes(String(data.idle_timeout_minutes ?? platformDefault));
    setInitialised(true);
  }, [loading, data, initialised, platformDefault]);

  function validate() {
    const v = parseInt(minutes, 10);
    if (isNaN(v) || v < 5 || v > 480) return "Idle timeout must be between 5 and 480 minutes.";
    return null;
  }

  async function handleSave() {
    const err = validate();
    if (err) { setSaveError(err); return; }
    setSaving(true); setSaved(false); setSaveError(null);
    try {
      const res  = await fetch(`${API_BASE}/tenant-settings/session`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ idle_timeout_minutes: parseInt(minutes, 10) }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.detail?.error?.code ?? "SAVE_ERROR";
        throw new Error(`[${code}] ${body?.detail?.error?.message ?? `HTTP ${res.status}`}`);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setMinutes(String(platformDefault));
    setSaveError(null);
  }

  const effectiveMinutes = data?.idle_timeout_minutes ?? platformDefault;
  const isTenantOverride = data?.is_tenant_override ?? false;

  return (
    <Card>
      <SectionHeader
        icon={ShieldCheck}
        title="Identity & Access"
        subtitle="SSO provider, MFA policy, and session configuration."
      />

      {loading && (
        <div className="animate-pulse space-y-2">
          <div className="h-4 w-2/3 rounded skeleton" />
          <div className="h-4 w-1/2 rounded skeleton" />
        </div>
      )}

      {error && !loading && (
        <div className="rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-3 py-2.5 text-tok-sm text-[rgb(var(--color-danger))]">
          Unable to load session settings.
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-5">

          {/* SSO / MFA — not yet built, honest stub inline */}
          <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-4">
            <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))] mb-1">
              SSO &amp; MFA
            </p>
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              {isSuperAdmin
                ? "SSO configuration and MFA enforcement — available in a future build."
                : `SSO provider: configured by your Super Admin.`}
            </p>
          </div>

          {/* Session idle timeout — inline single-row control */}
          <div className="divide-y divide-[rgb(var(--color-border))] rounded-xl border border-[rgb(var(--color-border))]">
            <div className="px-4 py-1">
              <FieldRow label={
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  Session idle timeout
                </span>
              }>
                {canEdit ? (
                  <div className="flex items-center justify-end gap-2 flex-wrap">
                    <input
                      type="number"
                      min={5}
                      max={480}
                      value={minutes}
                      onChange={(e) => { setMinutes(e.target.value); setSaveError(null); }}
                      className="w-20 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] text-right focus:outline-none"
                    />
                    <span className="text-tok-sm text-[rgb(var(--color-text-muted))] shrink-0">min</span>
                    <Button onClick={handleSave} disabled={saving}>
                      {saving ? "Saving…" : "Save"}
                    </Button>
                    {isTenantOverride && (
                      <Button variant="ghost" onClick={handleReset} disabled={saving}>
                        Reset
                      </Button>
                    )}
                    {saved && (
                      <span className="flex items-center gap-1 text-tok-sm text-[rgb(var(--color-success))]">
                        <CheckCircle2 className="h-4 w-4" /> Saved
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="font-medium text-[rgb(var(--color-text))]">
                    {effectiveMinutes} min
                    {!isTenantOverride && (
                      <span className="ml-2 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">
                        (platform default)
                      </span>
                    )}
                  </span>
                )}
              </FieldRow>
            </div>
          </div>

          {/* Validation error + contextual hints below the row */}
          {canEdit && (
            <div className="space-y-1.5 px-1">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                Users are warned 3 min before expiry. Default: {platformDefault} min · Range: 5–480 min.
                Takes effect for new sessions only.
              </p>
              {saveError && (
                <div className="flex items-start gap-2 rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{saveError}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Support & Diagnostics Section
// ---------------------------------------------------------------------------
function SupportSection({ role, tenantContext }) {
  const isSuperAdmin = role === "tenant_super_admin";
  const { data: diagData, loading, error, refetch } = useFetch(`${API_BASE}/diagnostics`);

  const tenantId    = tenantContext?.tenant_id    ?? diagData?.tenant_id    ?? "—";
  const appVersion  = diagData?.app_version                                 ?? "—";
  const apiVersion  = diagData?.api_version                                 ?? "—";
  const dbVersion   = diagData?.db_schema_version                           ?? "—";
  const healthStatus = diagData?.health_status                              ?? null;
  const supportEmail = diagData?.support_email    ?? "support@data-fit.com";

  return (
    <Card>
      <SectionHeader
        icon={LifeBuoy}
        title="Support & Diagnostics"
        subtitle="Tenant identifiers and version information for support requests."
      />

      {loading && (
        <div className="animate-pulse space-y-2">
          <div className="h-4 w-2/3 rounded skeleton" />
          <div className="h-4 w-1/2 rounded skeleton" />
        </div>
      )}

      {!loading && (
        <div className="divide-y divide-[rgb(var(--color-border))] rounded-xl border border-[rgb(var(--color-border))]">
          <div className="px-4 py-1">
            <FieldRow label="Tenant ID">
              <code className="font-mono text-tok-xs text-[rgb(var(--color-text))]">{tenantId}</code>
            </FieldRow>
            <FieldRow label="App version" value={appVersion} />

            {isSuperAdmin && (
              <>
                <FieldRow label="API version"       value={apiVersion} />
                <FieldRow label="DB schema version" value={dbVersion} />
                <FieldRow label="Health status">
                  {healthStatus ? (
                    <Pill tone={healthStatus === "healthy" ? "Complete" : "Warning"}>
                      {healthStatus}
                    </Pill>
                  ) : "—"}
                </FieldRow>
                <FieldRow label="Support email">
                  <a
                    href={`mailto:${supportEmail}`}
                    className="text-[rgb(var(--color-primary))] underline-offset-2 hover:underline"
                  >
                    {supportEmail}
                  </a>
                </FieldRow>
              </>
            )}
          </div>
        </div>
      )}

      {isSuperAdmin && (
        <div className="mt-4">
          <Button
            variant="ghost"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh diagnostics
          </Button>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// SettingsPanel root
// ---------------------------------------------------------------------------
export default function SettingsPanel({ role, tenantContext }) {
  const { user } = useAuth();
  const effectiveRole = role ?? user?.user_type ?? "comp_admin";
  const isSuperAdmin  = effectiveRole === "tenant_super_admin";

  return (
    <div className="space-y-6">
      {/* Licensing — always visible; role gates actions */}
      <LicensingSection role={effectiveRole} tenantContext={tenantContext} />

      {/* Localization — always visible; scope differs by role */}
      <LocalizationSection role={effectiveRole} />

      {/* Acknowledgment Thresholds — S616 — comp_admin + super_admin */}
      <AcknowledgmentThresholdsSection />

      {/* AI & Agent — stub */}
      <StubSection
        icon={Sparkles}
        title="AI & Agent"
        subtitle="Agent model, trust tier, and capability configuration."
        note={
          isSuperAdmin
            ? "Model selection, trust configuration, and capability toggles — available in a future build."
            : "Current AI model configuration is set by your Super Admin."
        }
      />

      {/* Integrations — stub */}
      <StubSection
        icon={Plug}
        title="Integrations"
        subtitle="Connected systems, connector health, and API credentials."
        note={
          isSuperAdmin
            ? "Connector management and credential rotation — available in a future build."
            : "Integration connector status is read-only. Contact your Super Admin to add or remove connectors."
        }
      />

      {/* Identity & Access — session idle timeout (S903) */}
      <IdentityAccessSection role={effectiveRole} />

      {/* Support & Diagnostics — always visible; Comp Admin sees subset */}
      <SupportSection role={effectiveRole} tenantContext={tenantContext} />

      {/* Branding — Super Admin only */}
      {isSuperAdmin && (
        <StubSection
          icon={Palette}
          title="Branding"
          subtitle="Tenant logo, color overrides, and white-label configuration."
          note="Branding configuration — available in a future build."
        />
      )}
    </div>
  );
}
